import { createTheme, CSSVariablesResolver, MantineColorsTuple } from '@mantine/core';

// Primary color palette - based on original #2F4550 (index 5)
const primary: MantineColorsTuple = [
  '#e8f0f3',
  '#d1e0e6',
  '#a3c1cd',
  '#7198ab',
  '#586F7C',
  '#2F4550',
  '#273a43',
  '#1f2f36',
  '#172429',
  '#0f191c',
];

// Sidebar/Navigation color palette - dark navy
const sidebar: MantineColorsTuple = [
  '#e8eaf0',
  '#c5cad8',
  '#a3aed0', // index 2 - nav text (muted)
  '#7b8ab8',
  '#5a6ea0',
  '#3d5291',
  '#2d3f70',
  '#1e2a3a', // index 7 - sidebar background
  '#1a2332', // index 8 - sidebar darker
  '#0f1620',
];

// Accent color palette - Mantine Teal
const accent: MantineColorsTuple = [
  '#e6fcf5',
  '#c3fae8',
  '#96f2d7',
  '#63e6be',
  '#38d9a9',
  '#20c997',
  '#12b886',
  '#0ca678',
  '#099268',
  '#087f5b',
];

// Info color palette - based on original #7198ab (index 4)
const info: MantineColorsTuple = [
  '#e8f4f8',
  '#d1e8f0',
  '#a3d1e1',
  '#7198ab',
  '#5a8a9f',
  '#4a7a8f',
  '#3d6a7f',
  '#305a6f',
  '#234a5f',
  '#163a4f',
];

export const cssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {
    '--app-sidebar-width': theme.other.sidebarWidth,
    '--font-heading': "'Literata', Georgia, 'Times New Roman', serif",
    '--font-body': "'Source Sans 3', -apple-system, BlinkMacSystemFont, sans-serif",
    '--app-glass-blur': 'blur(12px)',
    // Mantine's sm input height, for controls that must sit level with an input.
    '--app-input-height-sm': 'calc(2.25rem * var(--mantine-scale))',
  },
  light: {
    '--mantine-color-body': '#f5f3ef',
    // Glass / frosted surface
    '--app-glass-bg': 'rgba(255, 255, 255, 0.8)',
    // Card surface
    '--app-card-bg': 'rgba(255, 255, 255, 0.9)',
    '--app-card-hover-shadow':
      '0 8px 24px rgba(47, 69, 80, 0.08), 0 2px 8px rgba(47, 69, 80, 0.04)',
    // The "current" row of a table (the next Sunday), the nav's active tint softened
    '--app-row-highlight-bg': 'rgba(18, 184, 134, 0.08)',
    // Warm editorial surfaces
    '--app-warm-bg': '#faf8f5',
    '--app-warm-card-bg': 'rgba(250, 248, 245, 0.9)',
    '--app-warm-border': 'rgba(47, 69, 80, 0.08)',
    '--app-warm-shadow': '0 2px 12px rgba(47, 69, 80, 0.06), 0 1px 4px rgba(47, 69, 80, 0.04)',
    '--app-warm-shadow-hover': '0 8px 24px rgba(47, 69, 80, 0.1), 0 2px 8px rgba(47, 69, 80, 0.06)',
    // Sidebar
    '--app-sidebar-bg': sidebar[7],
    '--app-sidebar-bg-end': sidebar[8],
    '--app-sidebar-text': sidebar[2],
    '--app-sidebar-text-active': '#ffffff',
    '--app-sidebar-hover-bg': 'rgba(255, 255, 255, 0.1)',
    '--app-sidebar-border': 'rgba(255, 255, 255, 0.06)',
    '--app-sidebar-active-bg': 'rgba(18, 184, 134, 0.12)',
    '--app-sidebar-active-hover-bg': 'rgba(18, 184, 134, 0.16)',
  },
  dark: {
    '--mantine-color-body': 'var(--mantine-color-dark-7)',
    // Glass / frosted surface
    '--app-glass-bg': 'rgba(30, 30, 30, 0.8)',
    // Card surface
    '--app-card-bg': 'rgba(37, 38, 43, 0.9)',
    '--app-card-hover-shadow': '0 8px 24px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)',
    // The "current" row of a table (the next Sunday)
    '--app-row-highlight-bg': 'rgba(18, 184, 134, 0.14)',
    // Warm editorial surfaces (adapted for dark)
    '--app-warm-bg': 'var(--mantine-color-dark-7)',
    '--app-warm-card-bg': 'rgba(37, 38, 43, 0.9)',
    '--app-warm-border': 'rgba(255, 255, 255, 0.1)',
    '--app-warm-shadow': '0 2px 12px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1)',
    '--app-warm-shadow-hover': '0 8px 24px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(0, 0, 0, 0.15)',
    // Sidebar
    '--app-sidebar-bg': sidebar[7],
    '--app-sidebar-bg-end': sidebar[8],
    '--app-sidebar-text': sidebar[2],
    '--app-sidebar-text-active': '#ffffff',
    '--app-sidebar-hover-bg': 'rgba(255, 255, 255, 0.1)',
    '--app-sidebar-border': 'rgba(255, 255, 255, 0.06)',
    '--app-sidebar-active-bg': 'rgba(18, 184, 134, 0.12)',
    '--app-sidebar-active-hover-bg': 'rgba(18, 184, 134, 0.16)',
  },
});

export const theme = createTheme({
  // COLOR TOKENS
  primaryColor: 'primary',
  colors: {
    primary,
    sidebar,
    accent,
    info,
  },

  // SPACING TOKENS (referenced as var(--mantine-spacing-*))
  spacing: {
    xs: '0.5rem', // 8px
    sm: '0.75rem', // 12px
    md: '1rem', // 16px
    lg: '1.5rem', // 24px
    xl: '2rem', // 32px
  },

  // TYPOGRAPHY TOKENS
  fontFamily: "'Source Sans 3', -apple-system, BlinkMacSystemFont, sans-serif",
  headings: {
    fontFamily: "'Literata', Georgia, 'Times New Roman', serif",
    fontWeight: '600',
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },

  // RADIUS TOKENS
  radius: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },
  defaultRadius: 'md',

  // BREAKPOINT TOKENS
  breakpoints: {
    xs: '36em', // 576px
    sm: '48em', // 768px
    md: '62em', // 992px
    lg: '75em', // 1200px - laptop
    xl: '88em', // 1408px - desktop
  },

  // COMPONENT DEFAULTS (apply tokens to all instances)
  components: {
    Card: {
      defaultProps: {
        shadow: 'sm',
        radius: 'md',
        padding: 'md',
      },
    },
    Button: {
      defaultProps: {
        radius: 'sm',
      },
    },
    Paper: {
      defaultProps: {
        shadow: 'xs',
        radius: 'md',
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: 'sm',
      },
    },
  },

  // APP-SPECIFIC TOKENS (accessible via theme.other)
  other: {
    cardWidth: '24rem',
    cardMinHeight: '12rem',
    contentMaxWidth: '75rem',
    sidebarWidth: '240px',
  },
});

export default theme;
